import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';

interface ValidatableInputProps {
  type: 'email' | 'tel';
  value?: string;
  disabled?: boolean;
  skipValidationWhenBlank?: boolean;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  name?: string;
  required?: boolean;
}

// Modern validation using built-in HTML5 + custom logic
const validateEmail = (email: string): boolean => {
  if (!email.trim()) return false;
  // Email regex explanation:
  // ^[^\s@]+ - Start with one or more characters that are NOT whitespace or @
  // @ - Exactly one @ symbol (this prevents multiple @ symbols)
  // [^\s@]+ - One or more characters that are NOT whitespace or @ (domain name)
  // \. - Exactly one literal dot
  // [^\s@]+$ - End with one or more characters that are NOT whitespace or @ (TLD)
  // This ensures exactly one @ symbol and basic email structure
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validatePhone = (phone: string): boolean => {
  if (!phone.trim()) return false;
  // Extract digits only and check for 10+ digits
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10;
};

// Custom useId implementation for React 16 compatibility
let idCounter = 0;
const useCustomId = (): string => {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `validatable-input-${++idCounter}`;
  }
  return idRef.current;
};

export const ValidatableInput: React.FC<ValidatableInputProps> = ({
  type,
  value = '',
  disabled = false,
  skipValidationWhenBlank = false,
  onChange,
  placeholder,
  className = '',
  name,
  required = false,
}) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [hasError, setHasError] = useState(false);
  const [touched, setTouched] = useState(false);
  const generatedId = useCustomId();

  // Sync with external value changes (for Angular data binding)
  // Only sync when value changes externally, not when user is typing
  useEffect(() => {
    setCurrentValue(value || '');
  }, [value]);

  const validate = (inputValue: string): boolean => {
    const trimmedValue = inputValue.trim();
    const isEmpty = !trimmedValue;

    // If empty and required, fail validation
    if (isEmpty && required) {
      return false;
    }

    // If empty and skipValidationWhenBlank is set, pass validation
    if (isEmpty && skipValidationWhenBlank) {
      return true;
    }

    // If empty and not required, pass validation
    if (isEmpty && !required) {
      return true;
    }

    // For non-empty values, run type-specific validation
    switch (type) {
      case 'email':
        return validateEmail(trimmedValue);
      case 'tel':
        return validatePhone(trimmedValue);
      default:
        return true;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);

    const isValid = validate(newValue);

    // Only show error if field was touched
    if (touched) {
      setHasError(!isValid);
    }

    onChange?.(newValue);
  };

  const handleBlur = () => {
    setTouched(true);

    const isValid = validate(currentValue);
    setHasError(!isValid);
  };

  const getErrorMessage = (): string => {
    switch (type) {
      case 'email':
        return 'Please enter a valid email address.';
      case 'tel':
        return 'Please enter a valid phone number.';
      default:
        return 'Please enter a valid value.';
    }
  };

  const baseId = name || generatedId;
  const inputId = `validatable-${baseId}-input`;
  const errorId = `${baseId}-error`;

  const inputClasses = classNames('form-control', className, {
    'is-invalid': hasError && touched,
    'is-valid': touched && !hasError && currentValue.trim(),
  });

  return (
    <div className={classNames({ 'has-error': hasError && touched })}>
      <input
        type={type}
        value={currentValue}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClasses}
        name={name}
        required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        id={inputId}
        aria-invalid={hasError && touched}
        aria-describedby={hasError && touched ? errorId : undefined}
      />
      {hasError && touched && (
        <div className="invalid-feedback text-danger" id={errorId}>
          {getErrorMessage()}
        </div>
      )}
    </div>
  );
};
